import React, { useState, useEffect, useCallback } from 'react';
import { BarChart2, DollarSign, Cpu, Globe, Layers, TrendingUp, ChevronLeft, ChevronRight, Users, User, GitCompare } from 'lucide-react';
import { fetchAllUsage, fetchAllUsageRange, estimateCost } from '../lib/supabaseData';
import { useAuth } from '../contexts/AuthContext';
import { MARKET_FLAGS } from '../lib/constants';
import AdminUsersTab from './AdminUsersTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const USD_TO_SEK = 10.5;
const CODE_TO_DISP = { SE: 'Sverige', NO: 'Norge', FI: 'Finland', DE: 'Tyskland', DK: 'Danmark', NL: 'Nederländerna' };
const normMkt = m => CODE_TO_DISP[m] || m;

const MODEL_LABELS = { gemini: 'Gemini 2.5 Flash', claude: 'Claude Sonnet 4.5', gpt4o: 'GPT-4o' };
const MODEL_COLORS = { gemini: '#4f8ef7', claude: '#d97706', gpt4o: '#10b981' };

const MODULE_LABELS = {
  generate:   'Generera (enskild)',
  bulk:       'Massimport',
  translate:  'Översätt',
  autopilot:  'Autopilot',
  newsletter: 'Nyhetsbrev',
  image:      'Bildhantering',
};
const MODULE_COLORS = {
  generate:   '#6366f1',
  bulk:       '#0ea5e9',
  translate:  '#22c55e',
  autopilot:  '#f59e0b',
  newsletter: '#ec4899',
  image:      '#14b8a6',
};

function fmtSek(usd) {
  const sek = usd * USD_TO_SEK;
  if (sek < 0.001) return '< 0,001 kr';
  if (sek < 0.01)  return `${sek.toFixed(4).replace('.', ',')} kr`;
  return `${sek.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;
}

function MiniBar({ value, max, color }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="admin-bar-track">
      <div className="admin-bar-fill" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="admin-stat-card" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="admin-stat-icon" style={{ background: `${color}18`, color }}>
        <Icon size={18} />
      </div>
      <div>
        <p className="admin-stat-label">{label}</p>
        <p className="admin-stat-value">{value}</p>
        {sub && <p className="admin-stat-sub">{sub}</p>}
      </div>
    </div>
  );
}

const MONTH_NAMES = ['Januari','Februari','Mars','April','Maj','Juni','Juli','Augusti','September','Oktober','November','December'];

export default function AdminPage() {
  const { user, session } = useAuth();
  const accessToken = session?.access_token;
  const [tab, setTab] = useState('costs'); // 'costs' | 'users'
  const now = new Date();
  // 'rolling30' = senaste 30 dagar (default), 'month' = månadsvis navigering
  const [periodMode, setPeriodMode] = useState('rolling30');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cost reconciliation (BigQuery vs intern estimat)
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);

  const loadReconciliation = useCallback(async () => {
    if (!accessToken) return;
    setReconLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/cost-reconciliation?days=30&lag_days=2`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!r.ok) { setRecon(null); return; }
      setRecon(await r.json());
    } catch (_) {
      setRecon(null);
    } finally {
      setReconLoading(false);
    }
  }, [accessToken]);

  useEffect(() => { if (tab === 'costs') loadReconciliation(); }, [tab, loadReconciliation]);

  const load = useCallback(async () => {
    if (!user) return;
    if (tab !== 'costs') return;
    setLoading(true);
    setError(null);
    let data;
    if (periodMode === 'rolling30') {
      const to = new Date();
      const from = new Date(); from.setDate(from.getDate() - 30);
      data = await fetchAllUsageRange(from.toISOString(), to.toISOString());
    } else {
      data = await fetchAllUsage(year, month);
    }
    if (data === null) {
      setError('Användningstabellen finns inte ännu eller saknar admin-policy. Kör V19__usage_extension.sql i Supabase.');
      setRows([]);
    } else {
      setRows(data);
    }
    setLoading(false);
  }, [user, year, month, tab, periodMode]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
    if (isCurrentMonth) return;
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;

  const periodLabel = periodMode === 'rolling30'
    ? 'Senaste 30 dagarna'
    : `${MONTH_NAMES[month - 1]} ${year}`;

  // Aggregate stats — räkna om kostnaden från råa tokens med aktuell prissättning
  const costForRow = (r) => estimateCost(r.model || 'gemini', r.input_tokens || 0, r.output_tokens || 0);

  const totalCost = rows ? rows.reduce((s, r) => s + costForRow(r), 0) : 0;
  const totalGens = rows ? rows.length : 0;

  const byModel = rows ? rows.reduce((acc, r) => {
    const m = r.model || 'gemini';
    if (!acc[m]) acc[m] = { cost: 0, count: 0, inputTokens: 0, outputTokens: 0 };
    acc[m].cost += costForRow(r);
    acc[m].count += 1;
    acc[m].inputTokens += r.input_tokens || 0;
    acc[m].outputTokens += r.output_tokens || 0;
    return acc;
  }, {}) : {};

  const byModule = rows ? rows.reduce((acc, r) => {
    const k = r.module || 'Okänd';
    if (!acc[k]) acc[k] = { cost: 0, count: 0, inputTokens: 0, outputTokens: 0 };
    acc[k].cost += costForRow(r);
    acc[k].count += 1;
    acc[k].inputTokens += r.input_tokens || 0;
    acc[k].outputTokens += r.output_tokens || 0;
    return acc;
  }, {}) : {};

  const byUser = rows ? rows.reduce((acc, r) => {
    const k = r.user_email || 'Okänd';
    if (!acc[k]) acc[k] = { cost: 0, count: 0, inputTokens: 0, outputTokens: 0, lastSeen: null };
    acc[k].cost += costForRow(r);
    acc[k].count += 1;
    acc[k].inputTokens += r.input_tokens || 0;
    acc[k].outputTokens += r.output_tokens || 0;
    const ts = r.created_at ? new Date(r.created_at).getTime() : 0;
    if (!acc[k].lastSeen || ts > acc[k].lastSeen) acc[k].lastSeen = ts;
    return acc;
  }, {}) : {};

  const byMarket = rows ? rows.reduce((acc, r) => {
    const m = normMkt(r.market || 'Okänd');
    if (!acc[m]) acc[m] = { cost: 0, count: 0 };
    acc[m].cost += costForRow(r);
    acc[m].count += 1;
    return acc;
  }, {}) : {};

  const byCategory = rows ? rows.reduce((acc, r) => {
    const c = r.category || 'Okänd';
    if (!acc[c]) acc[c] = { cost: 0, count: 0 };
    acc[c].cost += costForRow(r);
    acc[c].count += 1;
    return acc;
  }, {}) : {};

  const maxModelCost  = Math.max(...Object.values(byModel).map(v => v.cost),  0.0001);
  const maxModuleCost = Math.max(...Object.values(byModule).map(v => v.cost), 0.0001);
  const maxUserCost   = Math.max(...Object.values(byUser).map(v => v.cost),   0.0001);
  const maxMarketCost = Math.max(...Object.values(byMarket).map(v => v.cost), 0.0001);
  const maxCatCost    = Math.max(...Object.values(byCategory).map(v => v.cost), 0.0001);

  return (
    <div className="page-scroll">
      <div className="page-header admin-page-header">
        <div>
          <h1 className="page-title">Admin</h1>
          <p className="page-subtitle">
            {tab === 'costs' ? `Tokenanvändning och beräknad kostnad — ${periodLabel}` : 'Hantera användare och åtkomst'}
          </p>
        </div>
        {tab === 'costs' && (
          <div className="admin-month-nav" data-testid="period-selector" style={{ gap: 8 }}>
            <button
              data-testid="period-30d"
              className={`tab-btn${periodMode === 'rolling30' ? ' tab-btn--active' : ''}`}
              onClick={() => setPeriodMode('rolling30')}
              style={{ fontSize: 12 }}
            >
              Senaste 30 dagar
            </button>
            <button
              data-testid="period-month"
              className={`tab-btn${periodMode === 'month' ? ' tab-btn--active' : ''}`}
              onClick={() => setPeriodMode('month')}
              style={{ fontSize: 12 }}
            >
              Månadsvis
            </button>
            {periodMode === 'month' && (
              <>
                <button className="admin-month-btn" onClick={prevMonth} aria-label="Föregående månad"><ChevronLeft size={16} /></button>
                <span className="admin-month-label">{MONTH_NAMES[month - 1]} {year}</span>
                <button className="admin-month-btn" onClick={nextMonth} disabled={isCurrentMonth} aria-label="Nästa månad"><ChevronRight size={16} /></button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tab-switcher" style={{ marginBottom: 24 }}>
        <button
          data-testid="admin-tab-costs"
          className={`tab-btn${tab === 'costs' ? ' tab-btn--active' : ''}`}
          onClick={() => setTab('costs')}
        >
          <BarChart2 size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
          Kostnadsöversikt
        </button>
        <button
          data-testid="admin-tab-users"
          className={`tab-btn${tab === 'users' ? ' tab-btn--active' : ''}`}
          onClick={() => setTab('users')}
        >
          <Users size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
          Användare
        </button>
      </div>

      {tab === 'users' ? (
        <AdminUsersTab />
      ) : (
        <>
          {error && (
            <div className="admin-notice" data-testid="admin-notice">{error}</div>
          )}

          {loading ? (
            <div className="admin-loading">Laddar användningsdata...</div>
          ) : (
            <>
              {/* KPI cards */}
              <div className="admin-stats-row">
                <StatCard icon={DollarSign} label="Total kostnad" value={fmtSek(totalCost)} sub={periodLabel} color="#6366f1" />
                <StatCard icon={TrendingUp} label="Totalt genereringar" value={totalGens.toLocaleString('sv-SE')} color="#22c55e" />
                <StatCard icon={Layers} label="Aktiva moduler" value={Object.keys(byModule).length} color="#ec4899" />
                <StatCard icon={User}   label="Aktiva användare" value={Object.keys(byUser).length} color="#f59e0b" />
              </div>

              {/* Cost reconciliation — Gemini intern vs GCP faktisk */}
              <div className="admin-section" data-testid="reconciliation-card">
                <div className="admin-recon-card" style={{
                  display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 24,
                  padding: '18px 22px', borderRadius: 12,
                  background: 'rgba(99,102,241,0.06)',
                  border: '1px solid rgba(99,102,241,0.18)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#a5b4fc' }}>
                    <GitCompare size={16} />
                    <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      Gemini kalibrering · senaste 30 dagar (−2 d eftersläp)
                    </span>
                  </div>
                  {reconLoading ? (
                    <span style={{ fontSize: 13, opacity: 0.7 }}>Laddar BigQuery...</span>
                  ) : recon ? (
                    <>
                      <span data-testid="recon-internal" style={{ fontSize: 14 }}>
                        <strong>Gemini intern:</strong>{' '}
                        {fmtSek(recon.gemini_internal?.usd ?? 0)}
                      </span>
                      <span style={{ opacity: 0.4 }}>·</span>
                      <span data-testid="recon-gcp" style={{ fontSize: 14 }}>
                        <strong>GCP-faktisk:</strong>{' '}
                        {recon.gcp_actual
                          ? (() => {
                              const cur = (recon.gcp_actual.currency || 'USD').toUpperCase();
                              const amt = recon.gcp_actual.usd;
                              // Normalisera till SEK för apples-to-apples med Gemini intern.
                              if (cur === 'SEK') {
                                return `${amt.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;
                              }
                              if (cur === 'USD') return fmtSek(amt);
                              // Okänd valuta — visa rått värde med valutakod.
                              return `${amt.toFixed(4)} ${cur}`;
                            })()
                          : <span style={{ color: '#f59e0b' }}>otillgänglig</span>}
                      </span>
                      {recon.gcp_error && (
                        <span title={recon.gcp_error} style={{ fontSize: 12, color: '#f59e0b', opacity: 0.85 }}>
                          ⚠ {recon.gcp_error.length > 80 ? recon.gcp_error.slice(0, 80) + '…' : recon.gcp_error}
                        </span>
                      )}
                      {recon.period && (
                        <span style={{ marginLeft: 'auto', fontSize: 12, opacity: 0.55 }}>
                          {recon.period.from} → {recon.period.to}
                        </span>
                      )}
                    </>
                  ) : (
                    <span style={{ fontSize: 13, opacity: 0.7 }}>Kunde inte hämta kalibreringsdata.</span>
                  )}
                </div>
              </div>

              {/* Per modul */}
              {Object.keys(byModule).length > 0 ? (
                <div className="admin-section">
                  <h2 className="admin-section-title">Kostnad per modul</h2>
                  <div className="admin-table" data-testid="module-breakdown">
                    <div className="admin-table-header">
                      <span>Modul</span><span>Genereringar</span><span>Tokens (in/ut)</span><span>Kostnad</span><span></span>
                    </div>
                    {Object.entries(byModule).sort((a,b) => b[1].cost - a[1].cost).map(([m, v]) => (
                      <div key={m} className="admin-table-row" data-testid={`module-row-${m}`}>
                        <span className="admin-model-cell">
                          <span className="admin-model-dot" style={{ background: MODULE_COLORS[m] || '#9ca3af' }} />
                          {MODULE_LABELS[m] || m}
                        </span>
                        <span>{v.count}</span>
                        <span className="admin-tokens">{v.inputTokens.toLocaleString('sv-SE')} / {v.outputTokens.toLocaleString('sv-SE')}</span>
                        <span>{fmtSek(v.cost)}</span>
                        <span style={{ minWidth: 120 }}><MiniBar value={v.cost} max={maxModuleCost} color={MODULE_COLORS[m] || '#9ca3af'} /></span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : rows && rows.length === 0 ? (
                <div className="admin-empty" data-testid="admin-empty">Inga genereringar loggade under {periodLabel.toLowerCase()}.</div>
              ) : null}

              {/* Per användare */}
              {Object.keys(byUser).length > 0 && (
                <div className="admin-section">
                  <h2 className="admin-section-title">Kostnad per användare</h2>
                  <div className="admin-table" data-testid="user-breakdown">
                    <div className="admin-table-header">
                      <span>Användare</span><span>Genereringar</span><span>Tokens (in/ut)</span><span>Kostnad</span><span></span>
                    </div>
                    {Object.entries(byUser).sort((a,b) => b[1].cost - a[1].cost).map(([u, v]) => (
                      <div key={u} className="admin-table-row" data-testid={`user-row-${u}`}>
                        <span className="admin-model-cell">
                          <User size={12} style={{ opacity: 0.6 }} />
                          {u}
                        </span>
                        <span>{v.count}</span>
                        <span className="admin-tokens">{v.inputTokens.toLocaleString('sv-SE')} / {v.outputTokens.toLocaleString('sv-SE')}</span>
                        <span>{fmtSek(v.cost)}</span>
                        <span style={{ minWidth: 120 }}><MiniBar value={v.cost} max={maxUserCost} color="#f59e0b" /></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Per modell */}
              {Object.keys(byModel).length > 0 && (
                <div className="admin-section">
                  <h2 className="admin-section-title">Kostnad per modell</h2>
                  <div className="admin-table" data-testid="model-breakdown">
                    <div className="admin-table-header">
                      <span>Modell</span><span>Genereringar</span><span>Tokens (in/ut)</span><span>Kostnad</span><span></span>
                    </div>
                    {Object.entries(byModel).sort((a,b) => b[1].cost - a[1].cost).map(([m, v]) => (
                      <div key={m} className="admin-table-row">
                        <span className="admin-model-cell">
                          <span className="admin-model-dot" style={{ background: MODEL_COLORS[m] || '#9ca3af' }} />
                          {MODEL_LABELS[m] || m}
                        </span>
                        <span>{v.count}</span>
                        <span className="admin-tokens">{v.inputTokens.toLocaleString('sv-SE')} / {v.outputTokens.toLocaleString('sv-SE')}</span>
                        <span>{fmtSek(v.cost)}</span>
                        <span style={{ minWidth: 120 }}><MiniBar value={v.cost} max={maxModelCost} color={MODEL_COLORS[m] || '#9ca3af'} /></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Per marknad */}
              {Object.keys(byMarket).length > 0 && (
                <div className="admin-section">
                  <h2 className="admin-section-title">Kostnad per marknad</h2>
                  <div className="admin-table" data-testid="market-breakdown">
                    <div className="admin-table-header">
                      <span>Marknad</span><span>Genereringar</span><span>Kostnad</span><span></span>
                    </div>
                    {Object.entries(byMarket).sort((a,b) => b[1].cost - a[1].cost).map(([m, v]) => (
                      <div key={m} className="admin-table-row">
                        <span>{MARKET_FLAGS[m] || ''} {m}</span>
                        <span>{v.count}</span>
                        <span>{fmtSek(v.cost)}</span>
                        <span style={{ minWidth: 120 }}><MiniBar value={v.cost} max={maxMarketCost} color="#6366f1" /></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Per kategori */}
              {Object.keys(byCategory).length > 0 && (
                <div className="admin-section">
                  <h2 className="admin-section-title">Kostnad per kategori</h2>
                  <div className="admin-table" data-testid="category-breakdown">
                    <div className="admin-table-header">
                      <span>Kategori</span><span>Genereringar</span><span>Kostnad</span><span></span>
                    </div>
                    {Object.entries(byCategory).sort((a,b) => b[1].cost - a[1].cost).map(([c, v]) => (
                      <div key={c} className="admin-table-row">
                        <span>{c}</span>
                        <span>{v.count}</span>
                        <span>{fmtSek(v.cost)}</span>
                        <span style={{ minWidth: 120 }}><MiniBar value={v.cost} max={maxCatCost} color="#22c55e" /></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
